import type { Span } from '@opentelemetry/api'
import { context, metrics, SpanStatusCode, trace } from '@opentelemetry/api'
import { ZoneContextManager } from '@opentelemetry/context-zone'
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction'
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics'
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import {
  ATTR_DEPLOYMENT_ENVIRONMENT_NAME,
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_NAMESPACE,
  ATTR_SERVICE_VERSION,
} from '@opentelemetry/semantic-conventions/incubating'
import { onCLS, onINP, onLCP, onTTFB } from 'web-vitals'
import { env } from './helpers/env'

let meterProviderRef: MeterProvider | null = null

export function initTracing() {
  console.log(`Initializing OpenTelemetry for ${env.otel.service.name}...`)

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: env.otel.service.name,
    [ATTR_SERVICE_VERSION]: env.otel.service.version,
    [ATTR_SERVICE_NAMESPACE]: env.otel.service.namespace,
    [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: env.otel.deploymentEnvironment,
  })

  const traceExporter = new OTLPTraceExporter({
    url: `${env.otel.endpoint}/v1/traces`,
  })

  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [new BatchSpanProcessor(traceExporter)],
  })

  provider.register({
    contextManager: new ZoneContextManager(),
  })

  const meterProvider = new MeterProvider({
    resource,
    readers: [
      new PeriodicExportingMetricReader({
        exporter: new OTLPMetricExporter({
          url: `${env.otel.endpoint}/v1/metrics`,
        }),
        exportIntervalMillis: 30000,
      }),
    ],
  })
  metrics.setGlobalMeterProvider(meterProvider)
  meterProviderRef = meterProvider

  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: [
          /^https?:\/\/localhost:5555/,
          /^https?:\/\/[^/]*\.api\./,
          env.api.url,
        ],
        clearTimingResources: true,
      }),
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls: [
          /^https?:\/\/localhost:5555/,
          /^https?:\/\/[^/]*\.api\./,
          env.api.url,
        ],
      }),
      new UserInteractionInstrumentation({
        eventNames: ['click', 'submit'],
      }),
    ],
  })

  setupWebVitals()
  setupErrorCapture()

  console.log('OpenTelemetry initialized successfully')
}

export function getTracer() {
  return trace.getTracer(env.otel.service.name, env.otel.service.version)
}

export function getMeter() {
  return metrics.getMeter(env.otel.service.name, env.otel.service.version)
}

export function withSpan<T>(name: string, fn: () => T): T {
  const tracer = getTracer()
  const span = tracer.startSpan(name)
  const ctx = trace.setSpan(context.active(), span)

  try {
    return context.with(ctx, fn)
  } catch (error) {
    recordError(span, error)
    throw error
  } finally {
    span.end()
  }
}

function recordError(span: Span, error: unknown) {
  if (error instanceof Error) {
    span.recordException(error)
  } else {
    span.recordException({ name: 'NonError', message: String(error) })
  }
  span.setStatus({ code: SpanStatusCode.ERROR })
}

// Web Vitals → OTEL gauges.
// One observable gauge per metric; the latest sample wins each export cycle.
function setupWebVitals() {
  const meter = getMeter()
  const latest: Record<string, { value: number, rating: string, id: string }> = {}

  const observe = (name: string) => meter.createObservableGauge(`web_vitals.${name.toLowerCase()}`, {
    description: `Core Web Vital: ${name}`,
  }).addCallback((result) => {
    const last = latest[name]
    if (!last) return
    result.observe(last.value, { rating: last.rating, id: last.id })
  })

  observe('LCP')
  observe('INP')
  observe('CLS')
  observe('TTFB')

  const record = (name: string) => (m: { value: number, rating: string, id: string }) => {
    latest[name] = { value: m.value, rating: m.rating, id: m.id }
  }

  onLCP(record('LCP'))
  onINP(record('INP'))
  onCLS(record('CLS'))
  onTTFB(record('TTFB'))
}

// Capture uncaught JS errors and unhandled promise rejections as spans.
function setupErrorCapture() {
  const tracer = getTracer()

  window.addEventListener('error', (event) => {
    const span = tracer.startSpan('window.error')
    span.setAttribute('error.message', event.message)
    span.setAttribute('error.filename', event.filename ?? '')
    span.setAttribute('error.line', event.lineno ?? 0)
    span.setAttribute('error.column', event.colno ?? 0)
    if (event.error instanceof Error) {
      span.recordException(event.error)
    }
    span.setStatus({ code: SpanStatusCode.ERROR })
    span.end()
  })

  window.addEventListener('unhandledrejection', (event) => {
    const span = tracer.startSpan('window.unhandledrejection')
    const reason = event.reason
    span.setAttribute('error.message', reason instanceof Error ? reason.message : String(reason))
    if (reason instanceof Error) {
      span.recordException(reason)
    }
    span.setStatus({ code: SpanStatusCode.ERROR })
    span.end()
  })
}

// Emit a span + metric for each SPA navigation. Wire this from the router.
let navigationCounter: ReturnType<ReturnType<typeof getMeter>['createCounter']> | null = null
export function trackNavigation(pathname: string, search: string) {
  const tracer = getTracer()
  const span = tracer.startSpan('navigation', {
    attributes: {
      'url.path': pathname,
      'url.query': search,
      'url.full': `${pathname}${search}`,
    },
  })
  span.end()
  navigationCounter ??= getMeter().createCounter('web.navigation.count', {
    description: 'Count of SPA route changes',
  })
  navigationCounter.add(1, { 'url.path': pathname })
}

export async function shutdownTelemetry() {
  await meterProviderRef?.forceFlush()
}
